import * as preact from "preact";

import * as modalController from "modalController";
import readlangClient from "readlangClient";
import type { User } from "stores/UserStore";
import { getUrlVar } from "urlUtils";
import ApplyPremiumDialog from "views/ApplyPremiumDialog";
import ChangeEmailDialog from "views/ChangeEmailDialog";
import MergeUserDialog from "views/MergeUserDialog";
import StreakDialog from "views/StreakDialog";
import UserStatsView from "views/UserStatsView";

/**
 * Borrows the user's identity in this admin session (see src/impersonation.js)
 * and reloads as them. The session is fully writable as that user.
 */
const impersonate = async (userID: string) => {
  await readlangClient.request("POST", `/api/admin/impersonate/${userID}`, {});
  window.location.href = "/";
};

// Types for props
type Props = {
  appRouter: any;
};

// Types for state
type State = {
  query: string;
  users?: User[];
  refreshKey: number;
};

// Bind generics to ExpandableProps and ExpandableState
export default class AdminUsersView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      query: "",
      users: undefined,
      refreshKey: 0,
    };
  }

  componentDidMount() {
    const searchParam = getUrlVar("search");
    if (searchParam) {
      this.setState({ query: searchParam }, () => {
        this.fetchUsers();
      });
    }
  }

  updateUrlWithSearch() {
    const url = new URL(window.location.href);
    if (this.state.query) {
      url.searchParams.set("search", this.state.query);
    } else {
      url.searchParams.delete("search");
    }
    history.replaceState(null, "", url.toString());
  }

  async fetchUsers() {
    this.updateUrlWithSearch();

    const data = (
      await readlangClient.request("GET", "/api/user/list", {
        query: this.state.query,
      })
    ).data;

    console.log("data: ", data);
    this.setState({ users: data.users, refreshKey: this.state.refreshKey + 1 });
  }

  render() {
    return (
      <div>
        <h1>
          <a href="/admin">Admin</a> - Users
        </h1>
        <div>
          <h2>Search by email, username, object ID, or Stripe Customer ID</h2>
          <p style={{ display: "flex" }}>
            <input
              type="text"
              placeholder="Enter user email, username or ID to search for"
              value={this.state.query}
              onInput={(event) => {
                this.setState({ query: event.currentTarget.value });
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  this.fetchUsers();
                }
              }}
            ></input>
            <button onClick={() => this.fetchUsers()}>Search</button>
          </p>
          {this.state.users !== undefined ? (
            <div>
              <p>
                Got {this.state.users.length} user
                {this.state.users.length !== 1 ? "s" : ""}
              </p>
              {this.state.users.map((user) => {
                return (
                  <div>
                    <h2>{user.email}</h2>
                    <UserStatsView
                      key={`${user._id}-${this.state.refreshKey}`}
                      userID={user._id}
                    />
                    <details>
                      <summary>Raw JSON</summary>
                      <pre>{JSON.stringify(user, null, 2)}</pre>
                    </details>
                    <div class="userActions">
                      <button
                        onClick={async () => {
                          await readlangClient.request(
                            "POST",
                            `/api/user/${user._id}/cancelSubscriptionImmediately`,
                            {}
                          );
                          this.fetchUsers();
                        }}
                      >
                        Unsubscribe (make sure to refund on Stripe too)
                      </button>
                      <button
                        onClick={() => {
                          modalController.show(
                            <ApplyPremiumDialog
                              userID={user._id}
                              onApplied={() => this.fetchUsers()}
                            />
                          );
                        }}
                      >
                        Apply Premium...
                      </button>
                      <button
                        onClick={() => {
                          modalController.show(
                            <MergeUserDialog
                              targetUserID={user._id}
                              targetEmail={user.email}
                              onMergeComplete={() => this.fetchUsers()}
                            />
                          );
                        }}
                      >
                        Merge...
                      </button>
                      <button
                        onClick={() => {
                          modalController.show(
                            <ChangeEmailDialog
                              userID={user._id}
                              currentEmail={user.email}
                              onEmailChanged={() => this.fetchUsers()}
                            />
                          );
                        }}
                      >
                        Change Email...
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `Anything you do will really change ${user.email}'s account. Continue?`
                            )
                          ) {
                            impersonate(user._id);
                          }
                        }}
                      >
                        Log in as...
                      </button>
                      <button
                        onClick={() => {
                          modalController.show(
                            <StreakDialog
                              userID={user._id}
                              email={user.email}
                              onStreakUpdated={() => this.fetchUsers()}
                            />
                          );
                        }}
                      >
                        Streak...
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}
